import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  Bell,
  Send,
  Loader2,
  Factory,
  Building2,
  Globe,
  CheckCircle2,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { formatApiError } from "@/lib/errors";
import { useAgency } from "@/context/AgencyContext";

const MAX_TITLE = 100;
const MAX_BODY = 500;

function fmtTs(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

/** Super-admin-only compose + recent-history view. */
export default function NotificationsAdminPage() {
  const { agencies } = useAgency();
  const [manufacturers, setManufacturers] = useState([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [targetType, setTargetType] = useState("all"); // "all" | "agency" | "manufacturer"
  const [targetId, setTargetId] = useState("");
  const [previewCount, setPreviewCount] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [recent, setRecent] = useState([]);
  const [lastResult, setLastResult] = useState(null);
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [testingSelf, setTestingSelf] = useState(false);
  const [alsoSendEmail, setAlsoSendEmail] = useState(false);
  const [deletingNotifId, setDeletingNotifId] = useState(null);
  const [confirmDeleteNotif, setConfirmDeleteNotif] = useState(null);

  const loadDiag = () => {
    setDiagLoading(true);
    api
      .get("/notifications/diagnostics")
      .then(({ data }) => setDiag(data))
      .catch(() => setDiag(null))
      .finally(() => setDiagLoading(false));
  };

  const onTestSelf = async () => {
    setTestingSelf(true);
    try {
      const { data } = await api.post("/notifications/test-self");
      if (data.ok) {
        toast.success(
          `Self-test push sent · ${data.delivered}/${data.tokens_tried} device(s) accepted by FCM`,
          { duration: 8000 }
        );
      } else if (data.reason === "no_device_tokens_registered_for_calling_user") {
        toast.error(
          "Your account has no registered devices. Sign in on your phone, accept the notification prompt, then try again.",
          { duration: 10000 }
        );
      } else {
        const firstErr =
          data.results?.find((r) => !r.ok)?.error || "Unknown FCM error";
        toast.error(`Self-test failed: ${firstErr}`, { duration: 10000 });
      }
      loadDiag();
    } catch (err) {
      toast.error(formatApiError(err, "Self-test failed"));
    } finally {
      setTestingSelf(false);
    }
  };

  // Load manufacturer list once.
  useEffect(() => {
    api.get("/manufacturers").then(({ data }) => setManufacturers(data || []));
  }, []);

  const loadRecent = () =>
    api
      .get("/notifications", { params: { limit: 20 } })
      .then(({ data }) => setRecent(data || []))
      .catch(() => setRecent([]));

  const handleDeleteNotification = async (n) => {
    setDeletingNotifId(n.id);
    try {
      const { data } = await api.delete(`/notifications/${n.id}`);
      setRecent((prev) => prev.filter((r) => r.id !== n.id));
      const removed = data?.inbox_rows_removed ?? 0;
      toast.success(
        removed > 0
          ? `Deleted · also cleared ${removed} inbox row${removed === 1 ? "" : "s"}`
          : "Notification deleted"
      );
    } catch (err) {
      toast.error(formatApiError(err, "Delete failed"));
    } finally {
      setDeletingNotifId(null);
      setConfirmDeleteNotif(null);
    }
  };

  useEffect(() => {
    loadRecent();
    loadDiag();
  }, []);

  // Auto-clear stale preview when target changes
  useEffect(() => {
    setPreviewCount(null);
  }, [targetType, targetId]);

  const targetOptions =
    targetType === "agency"
      ? (agencies || []).map((a) => ({ id: a.id, name: a.name }))
      : targetType === "manufacturer"
      ? manufacturers.map((m) => ({ id: m.id, name: m.name }))
      : [];

  const targetReady =
    targetType === "all" || (targetId && targetId.trim().length > 0);
  const canPreview = targetReady;
  const canSend =
    targetReady && title.trim().length > 0 && body.trim().length > 0;

  const buildPayload = () => ({
    title: title.trim(),
    body: body.trim(),
    target_type: targetType,
    target_id: targetType === "all" ? null : targetId,
    channels: alsoSendEmail ? ["push", "email"] : ["push"],
  });

  const onPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post(
        "/notifications/preview-count",
        buildPayload()
      );
      setPreviewCount(data.recipient_count);
    } catch (err) {
      toast.error(formatApiError(err, "Could not compute recipient count"));
    } finally {
      setPreviewing(false);
    }
  };

  const onSendConfirmed = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/notifications/send", buildPayload());
      setLastResult(data);
      const inAppCount = data.recipient_count;
      const pushCount = data.delivered_count || 0;
      const withDevice = data.with_device_count || 0;
      const emailSent = data.email_sent_count || 0;
      if (pushCount > 0) {
        toast.success(
          `Delivered to ${inAppCount} ${inAppCount === 1 ? "user" : "users"} · ${pushCount} native push, ${
            inAppCount - pushCount
          } in-app inbox${emailSent > 0 ? ` · ${emailSent} email` : ""}`
        );
      } else if (withDevice === 0) {
        toast.success(
          `Delivered to ${inAppCount} ${inAppCount === 1 ? "user" : "users"} (in-app inbox)${emailSent > 0 ? ` · ${emailSent} email` : ""}. No native devices registered yet — users will see a red bell + toast when they next open the app.`,
          { duration: 8000 }
        );
      } else {
        toast.success(
          `Delivered to ${inAppCount} ${inAppCount === 1 ? "user" : "users"} (in-app inbox)${emailSent > 0 ? ` · ${emailSent} email` : ""}. ${withDevice} had a native device but FCM push failed — check Firebase config.`,
          { duration: 8000 }
        );
      }
      setTitle("");
      setBody("");
      setPreviewCount(null);
      setAlsoSendEmail(false);
      loadRecent();
    } catch (err) {
      toast.error(formatApiError(err, "Send failed"));
    } finally {
      setSending(false);
      setConfirmOpen(false);
    }
  };

  const targetLabel =
    targetType === "all"
      ? "Everyone"
      : targetOptions.find((o) => o.id === targetId)?.name || "—";

  return (
    <div className="space-y-6" data-testid="notifications-admin-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono-data flex items-center gap-1.5">
            <Bell className="w-3 h-3" />
            Notifications
          </div>
          <h1 className="mt-1 text-3xl font-display font-bold tracking-tight text-slate-900">
            Send a push notification
          </h1>
          <p className="mt-2 text-sm text-muted-foreground max-w-xl">
            Compose a broadcast to all users, everyone in a specific rep
            agency, or every distributor that has access to a specific
            manufacturer. The notification is delivered to the recipient&apos;s
            phone AND added to their in-app inbox.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={onTestSelf}
          disabled={testingSelf}
          className="rounded-sm"
          data-testid="push-self-test-btn"
        >
          {testingSelf && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
          Send test push to my devices
        </Button>
      </div>

      {/* Diagnostics strip */}
      <div
        className="bg-slate-50 border border-border rounded-sm px-4 py-3 text-xs"
        data-testid="push-diagnostics"
      >
        {diagLoading || !diag ? (
          <span className="text-muted-foreground">Loading push diagnostics…</span>
        ) : (
          <div className="flex items-center gap-4 flex-wrap">
            <span className="font-mono-data uppercase tracking-wider text-[10px] text-muted-foreground">
              Push status
            </span>
            <span data-testid="diag-fcm">
              FCM:&nbsp;
              <strong
                className={diag.fcm_initialized ? "text-green-700" : "text-red-700"}
              >
                {diag.fcm_initialized ? "initialized" : "NOT initialized"}
              </strong>
            </span>
            <span data-testid="diag-total-tokens">
              Registered devices:&nbsp;
              <strong>{diag.total_tokens}</strong>
              &nbsp;across {diag.distinct_users_with_token} user
              {diag.distinct_users_with_token === 1 ? "" : "s"}
            </span>
            {(diag.by_platform || []).map((p) => (
              <span key={p.platform} data-testid={`diag-platform-${p.platform}`}>
                {p.platform}: <strong>{p.tokens}</strong>
                {p.platform === "ios" && !p.looks_like_fcm_token && (
                  <span className="ml-1 text-amber-700">
                    (⚠ looks like raw APNs hex, not FCM — see IOS_BUILD.md)
                  </span>
                )}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Compose */}
        <div className="lg:col-span-2 space-y-4 bg-white border border-border rounded-sm p-5">
          {/* Target */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground font-mono-data">
              Send to
            </Label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              <TargetCard
                active={targetType === "all"}
                icon={Globe}
                label="Everyone"
                onClick={() => {
                  setTargetType("all");
                  setTargetId("");
                }}
                testId="target-all"
              />
              <TargetCard
                active={targetType === "agency"}
                icon={Building2}
                label="By rep agency"
                onClick={() => setTargetType("agency")}
                testId="target-agency"
              />
              <TargetCard
                active={targetType === "manufacturer"}
                icon={Factory}
                label="By manufacturer"
                onClick={() => setTargetType("manufacturer")}
                testId="target-manufacturer"
              />
            </div>
            {targetType !== "all" && (
              <div className="mt-2">
                <Select value={targetId} onValueChange={setTargetId}>
                  <SelectTrigger
                    className="rounded-sm"
                    data-testid="target-select"
                  >
                    <SelectValue
                      placeholder={
                        targetType === "agency"
                          ? "Pick a rep agency…"
                          : "Pick a manufacturer…"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {targetOptions.map((o) => (
                      <SelectItem key={o.id} value={o.id}>
                        {o.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Title */}
          <div>
            <Label
              htmlFor="notif-title"
              className="text-xs uppercase tracking-wider text-muted-foreground font-mono-data"
            >
              Title{" "}
              <span className="text-[10px] normal-case">
                ({title.length}/{MAX_TITLE})
              </span>
            </Label>
            <Input
              id="notif-title"
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, MAX_TITLE))}
              placeholder="e.g. New TCP rebate just launched"
              className="mt-1 rounded-sm"
              data-testid="notif-title"
            />
          </div>

          {/* Body */}
          <div>
            <Label
              htmlFor="notif-body"
              className="text-xs uppercase tracking-wider text-muted-foreground font-mono-data"
            >
              Message{" "}
              <span className="text-[10px] normal-case">
                ({body.length}/{MAX_BODY})
              </span>
            </Label>
            <Textarea
              id="notif-body"
              value={body}
              onChange={(e) => setBody(e.target.value.slice(0, MAX_BODY))}
              placeholder="Keep it concise. Push notifications look best at 2–3 short lines."
              rows={4}
              className="mt-1 rounded-sm resize-none"
              data-testid="notif-body"
            />
          </div>

          {/* Channels */}
          <div className="pt-3 border-t border-border">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono-data mb-2">
              Delivery channels
            </div>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-not-allowed opacity-70 mr-6">
              <input
                type="checkbox"
                checked
                disabled
                className="accent-slate-900"
                data-testid="channel-push-checkbox"
              />
              Push notification (in-app inbox + native push)
            </label>
            <label
              className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer"
              data-testid="channel-email-label"
            >
              <input
                type="checkbox"
                checked={alsoSendEmail}
                onChange={(e) => setAlsoSendEmail(e.target.checked)}
                className="accent-slate-900"
                data-testid="channel-email-checkbox"
              />
              Also send as email
            </label>
            {alsoSendEmail && (
              <div className="mt-2 text-xs text-muted-foreground">
                Recipients who've turned off email notifications in their Profile
                won't receive this via email — they'll still see the in-app inbox
                entry and the native push.
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between gap-2 pt-2 border-t border-border flex-wrap">
            <div className="text-xs text-muted-foreground">
              {previewCount !== null ? (
                <span
                  className="inline-flex items-center gap-1.5 text-slate-900 font-medium"
                  data-testid="preview-count"
                >
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                  Will reach{" "}
                  <span className="font-mono-data font-bold">
                    {previewCount}
                  </span>{" "}
                  user{previewCount === 1 ? "" : "s"}
                </span>
              ) : (
                <span>Click &quot;Preview recipients&quot; to see who will receive this.</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={onPreview}
                disabled={!canPreview || previewing}
                className="rounded-sm"
                data-testid="preview-btn"
              >
                {previewing && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                Preview recipients
              </Button>
              <Button
                onClick={() => setConfirmOpen(true)}
                disabled={!canSend || sending}
                className="rounded-sm bg-slate-900 hover:bg-slate-800"
                data-testid="send-btn"
              >
                <Send className="w-3.5 h-3.5 mr-1.5" />
                Send notification
              </Button>
            </div>
          </div>
        </div>

        {/* Recent sends */}
        <div className="bg-white border border-border rounded-sm p-5">
          <h3 className="font-display text-sm font-semibold tracking-tight mb-3">
            Recent sends
          </h3>
          {recent.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">
              No notifications sent yet.
            </p>
          ) : (
            <ul className="space-y-3 max-h-[520px] overflow-auto">
              {recent.map((n) => (
                <li
                  key={n.id}
                  className="group text-xs border-l-2 border-amber-400 pl-3 py-1 relative"
                  data-testid={`recent-notif-${n.id}`}
                >
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-900 truncate">
                        {n.title}
                      </div>
                      <div className="text-muted-foreground line-clamp-2">
                        {n.body}
                      </div>
                      <div className="mt-1 flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground font-mono-data">
                        <span>{fmtTs(n.sent_at)}</span>
                        <span>·</span>
                        <span>
                          {n.recipient_count} reached
                          {n.delivered_count > 0 &&
                            ` · ${n.delivered_count} push`}
                          {n.email_sent_count > 0 &&
                            ` · ${n.email_sent_count} email`}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setConfirmDeleteNotif(n)}
                      disabled={deletingNotifId === n.id}
                      title="Delete notification"
                      aria-label="Delete notification"
                      className="shrink-0 w-6 h-6 flex items-center justify-center rounded-sm text-slate-400 opacity-0 group-hover:opacity-100 hover:text-rose-600 hover:bg-rose-50 transition-all disabled:opacity-50 focus:opacity-100"
                      data-testid={`delete-notif-${n.id}`}
                    >
                      {deletingNotifId === n.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Send this notification?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="block">
                Target: <strong>{targetLabel}</strong>
                {previewCount !== null && (
                  <>
                    {" "}
                    · <strong>{previewCount}</strong> recipient
                    {previewCount === 1 ? "" : "s"}
                  </>
                )}
              </span>
              <span className="block mt-2 font-semibold text-slate-900">
                {title}
              </span>
              <span className="block text-sm text-slate-700">{body}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={sending} className="rounded-sm">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={onSendConfirmed}
              disabled={sending}
              className="bg-slate-900 hover:bg-slate-800 rounded-sm"
              data-testid="confirm-send-btn"
            >
              {sending && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
              Send now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {lastResult && (
        <div className="sr-only" aria-live="polite" data-testid="last-result">
          Sent to {lastResult.recipient_count} · delivered to {lastResult.delivered_count}
        </div>
      )}

      <AlertDialog
        open={!!confirmDeleteNotif}
        onOpenChange={(o) => !o && setConfirmDeleteNotif(null)}
      >
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this notification?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove "<strong>{confirmDeleteNotif?.title}</strong>"
              from your Recent Sends AND from every recipient's in-app inbox.
              Any push notifications or emails that were already delivered can't
              be recalled. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDeleteNotification(confirmDeleteNotif)}
              className="bg-destructive hover:bg-red-600 text-white rounded-sm"
              data-testid="confirm-delete-notification"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function TargetCard({ active, icon: Icon, label, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`flex items-center gap-2 px-3 py-2.5 rounded-sm border text-sm text-left transition-colors ${
        active
          ? "border-slate-900 bg-slate-900 text-white"
          : "border-border bg-white text-slate-700 hover:bg-slate-50"
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}
